import { NgModule } from '@angular/core';
import { PreloadAllModules, RouterModule, Routes } from '@angular/router';
import { IntroGuard } from '../guards/intro.guard';
import { AutoLoginGuard } from '../guards/auto-login.guard';
import { AuthGuard } from './auth/auth.guard';
import { PrivilegeGuard } from '../guards/privilege.guard';
import { NoShowGuard } from '../guards/no-show.guard';
import { environment } from '../environments/environment';
import { ExternalRehearsalPage } from './rehearsal/external-rehearsal.page';
import { Privileges } from '../models/privileges';
import { ShowFlagGuard } from '../guards/show-flag.guard';
import { ForceChangePasswordGuard } from '../guards/force-change-password.guard';

const pathOnBoot = 'dashboard';

const routes: Routes = [
    {
        path: '',
        redirectTo: pathOnBoot,
        pathMatch: 'full'
    },
    {
        path: 'dashboard',
        loadChildren: () =>
            import('./dashboard/dashboard.module').then(m => m.DashboardPageModule),
        canLoad: [AuthGuard, ForceChangePasswordGuard, NoShowGuard, IntroGuard],
        canActivate: [AuthGuard, ForceChangePasswordGuard, NoShowGuard]
    },
    {
        path: 'logboek',
        loadChildren: () =>
            import('./logboek/logboek-routing.module').then(
                m => m.LogboekRoutingModule
            ),
        canLoad: [AuthGuard, ForceChangePasswordGuard, NoShowGuard],
        canActivate: [AuthGuard, ForceChangePasswordGuard, NoShowGuard, ShowFlagGuard],
        data: { showFlag: 'hasLog' }
    },
    {
        // Public Play Console / GDPR-required data deletion info page.
        // No guards — Google's reviewers and end users must reach this without auth.
        path: 'account-deletion',
        loadChildren: () =>
            import('./account-deletion/account-deletion.module').then(m => m.AccountDeletionPageModule)
    },
    {
        path: 'auth',
        loadChildren: () =>
            import('./auth/auth.module').then(m => m.AuthPageModule),
        canLoad: [AutoLoginGuard]
    },
    {
        path: 'no-show',
        loadChildren: () =>
            import('./no-show/no-show.module').then(m => m.NoShowPageModule),
        canLoad: [AuthGuard, ForceChangePasswordGuard]
    },
    {
        path: 'showreports',
        loadChildren: () =>
            import('./showreport/showreport-routing.module').then(
                m => m.ShowreportRoutingModule
            ),
        canLoad: [AuthGuard, ForceChangePasswordGuard, NoShowGuard],
        canActivate: [AuthGuard, ForceChangePasswordGuard, NoShowGuard, ShowFlagGuard],
        data: { showFlag: 'hasShowreports' }
    },
    // Backward-compat redirects for the legacy singular URLs (old emails,
    // bookmarks, deep links). Order matters: literal segments first, then
    // the more specific edit redirect, then the catch-all `:id`.
    { path: 'showreport', redirectTo: 'showreports', pathMatch: 'full' },
    { path: 'showreport/add', redirectTo: 'showreports/add', pathMatch: 'full' },
    { path: 'showreport/edit/:id', redirectTo: 'showreports/:id/edit', pathMatch: 'full' },
    { path: 'showreport/:id', redirectTo: 'showreports/:id', pathMatch: 'full' },
    {
        path: 'tasks',
        loadChildren: () =>
            import('./tasks/tasks-routing.module').then(m => m.TasksPageRoutingModule),
        canLoad: [AuthGuard, ForceChangePasswordGuard, NoShowGuard],
        canActivate: [AuthGuard, ForceChangePasswordGuard, NoShowGuard, ShowFlagGuard],
        data: { showFlag: 'hasTasks' }
    },
    {
        path: 'intro',
        loadChildren: () =>
            import('./intro/intro.module').then(m => m.IntroPageModule),
        canLoad: [AuthGuard, ForceChangePasswordGuard]
    },
    {
        // Public landing page reached via the confirmation link in the
        // "bevestig je e-mailadres" email. No AuthGuard — the visitor may
        // be unauthenticated (fresh signup, incognito click) or already
        // logged in (legacy users adding their email mid-session).
        path: 'email-confirmed',
        loadChildren: () =>
            import('./email-confirmed/email-confirmed.module').then(m => m.EmailConfirmedPageModule),
    },
    {
        // Reached when an admin has issued a temp password (mustChangePassword=true).
        // The nav-redirect in app.component routes the user here on login; the page
        // calls the backend force-change endpoint and on success the user lands on
        // /dashboard. No AuthGuard route-config — the page calls an authenticated
        // endpoint that fails closed if the JWT is missing or the flag isn't set.
        path: 'force-change-password',
        loadChildren: () =>
            import('./force-change-password/force-change-password.module').then(m => m.ForceChangePasswordPageModule),
    },
    {
        path: 'bestellijst',
        loadChildren: () =>
            import('./orders/bestellijst/bestellijst.module').then(
                m => m.BestellijstPageModule
            ),
        canLoad: [AuthGuard, ForceChangePasswordGuard, NoShowGuard],
        canActivate: [AuthGuard, ForceChangePasswordGuard, NoShowGuard]
    },
    {
        path: 'orders',
        loadChildren: () =>
            import('./orders/orders/orders.module').then(m => m.OrdersPageModule),
        canLoad: [AuthGuard, ForceChangePasswordGuard, NoShowGuard],
        canActivate: [AuthGuard, ForceChangePasswordGuard, NoShowGuard]
    },
    {
        path: 'ontruiming',
        loadChildren: () =>
            import('./ontruiming/ontruiming.module').then(
                m => m.OntruimingPageModule
            ),
        canLoad: [AuthGuard, ForceChangePasswordGuard, NoShowGuard],
        canActivate: [AuthGuard, ForceChangePasswordGuard, NoShowGuard, ShowFlagGuard],
        data: { showFlag: 'hasEvacuationInstructions' }
    },
    {
        path: 'changementen',
        loadChildren: () =>
            import('./changementen/changementsnlijst.module').then(
                m => m.ChangementsnlijstPageModule
            ),
        canLoad: [AuthGuard, ForceChangePasswordGuard, NoShowGuard],
        canActivate: [AuthGuard, ForceChangePasswordGuard, NoShowGuard, ShowFlagGuard],
        data: { showFlag: 'hasSceneryChangeList' }
    },
    {
        path: 'showstop',
        loadChildren: () =>
            import('./showstop/showstop.module').then(m => m.ShowstopPageModule),
        canLoad: [AuthGuard, ForceChangePasswordGuard, NoShowGuard],
        canActivate: [AuthGuard, ForceChangePasswordGuard, NoShowGuard, ShowFlagGuard],
        data: { showFlag: 'hasShowStopProcedure' }
    },
    {
        path: 'changelog',
        loadChildren: () =>
            import('./changelog/changelog.module').then(m => m.ChangelogPageModule),
        canLoad: [AuthGuard, ForceChangePasswordGuard]
    },
    {
        path: 'showtimer',
        loadChildren: () =>
            import('./showtimer/showtimer.module').then(m => m.ShowTimerPageModule),
        canLoad: [AuthGuard, ForceChangePasswordGuard, NoShowGuard],
        canActivate: [AuthGuard, ForceChangePasswordGuard, NoShowGuard, ShowFlagGuard],
        data: { showFlag: 'hasShowTimer' }
    },
    {
        path: 'opstartprocedure',
        loadChildren: () => import('./opstartprocedure/opstartprocedure.module').then(m => m.OpstartprocedurePageModule),
        canLoad: [AuthGuard, ForceChangePasswordGuard, NoShowGuard],
        canActivate: [AuthGuard, ForceChangePasswordGuard, NoShowGuard, ShowFlagGuard],
        data: { showFlag: 'hasStartupProcedure' }
    },
    {
        path: 'maintenance',
        loadChildren: () =>
            import('./maintenance/maintenance.module').then(m => m.MaintenancePageModule),
        canLoad: [AuthGuard, ForceChangePasswordGuard, NoShowGuard],
        canActivate: [AuthGuard, ForceChangePasswordGuard, NoShowGuard, ShowFlagGuard],
        data: { showFlag: 'hasMaintenance' }
    },
    {
        path: 'magicaluna/performer-fly',
        loadChildren: () =>
            import('./magicaluna/performer-fly-inspection/performer-fly-inspection.routes').then(
                m => m.PERFORMER_FLY_INSPECTION_ROUTES
            ),
        canLoad: [AuthGuard, ForceChangePasswordGuard, NoShowGuard],
        // Viewing the inspection list / details is open to every user
        // linked to the show that has the Performer Fly flag on — show
        // membership is the only gate. CREATE + DELETE actions inside
        // the page still check their own privileges in the UI.
        canActivate: [AuthGuard, ForceChangePasswordGuard, NoShowGuard, ShowFlagGuard],
        data: {
            showFlag: 'hasPerformerFlyInspections'
        }
    },
    {
        path: 'protocols',
        loadChildren: () => import('./protocols/protocols.module').then(m => m.ProtocolsPageModule),
        canLoad: [AuthGuard, ForceChangePasswordGuard, NoShowGuard],
        canActivate: [AuthGuard, ForceChangePasswordGuard, NoShowGuard, ShowFlagGuard],
        data: { showFlag: 'hasProtocols' }
    },
    {
        path: 'contacts',
        loadComponent: () =>
            import('./contacts/contacts.page').then(m => m.ContactsPage),
        canLoad: [AuthGuard, ForceChangePasswordGuard, NoShowGuard],
        canActivate: [AuthGuard, ForceChangePasswordGuard, NoShowGuard, ShowFlagGuard],
        data: { showFlag: 'hasContactList' }
    },
    {
        path: 'password-reset',
        loadChildren: () => import('./password-reset/password-reset.module').then(m => m.PasswordResetPageModule)
    },
    {
        path: 'performances',
        loadChildren: () => import('./performances/performances/performances.module').then(m => m.PerformancesPageModule),
        canLoad: [AuthGuard, ForceChangePasswordGuard, NoShowGuard],
        canActivate: [AuthGuard, ForceChangePasswordGuard, NoShowGuard, ShowFlagGuard],
        data: { showFlag: 'hasPerformances' }
    },
    {
        path: 'performance-edit',
        loadChildren: () => import('./performances/performances/performances.module').then(m => m.PerformancesPageModule),
        canLoad: [AuthGuard, ForceChangePasswordGuard, NoShowGuard],
        canActivate: [AuthGuard, ForceChangePasswordGuard, NoShowGuard, ShowFlagGuard],
        data: { showFlag: 'hasPerformances' }
    },
    {
        path: 'planner',
        loadChildren: () => import('./planner/planner.module').then(m => m.PlannerPageModule),
        canLoad: [AuthGuard, ForceChangePasswordGuard, NoShowGuard],
        canActivate: [AuthGuard, ForceChangePasswordGuard, NoShowGuard, ShowFlagGuard],
        data: { showFlag: 'hasPlanner' }
    },
    {
        path: 'settings',
        loadChildren: () => import('./settings/settings.module').then(m => m.SettingsPageModule),
        canLoad: [AuthGuard, ForceChangePasswordGuard]
    },
    {
        path: 'users',
        loadChildren: () => import('./users/users.module').then(m => m.UsersPageModule),
        canLoad: [AuthGuard, ForceChangePasswordGuard]
    },
    {
        path: 'rehearsal/external/:token',
        component: ExternalRehearsalPage
    },
    {
        path: 'rehearsal',
        loadChildren: () => import('./rehearsal/rehearsal-routing.module').then(m => m.RehearsalRoutingModule),
        canLoad: [AuthGuard, ForceChangePasswordGuard, NoShowGuard],
        canActivate: [AuthGuard, ForceChangePasswordGuard, NoShowGuard]
    },
    {
        // Admin shows page: NO NoShowGuard - admins need to manage show assignments
        path: 'admin/shows',
        loadChildren: () => import('./admin/shows/shows.module').then(m => m.ShowsModule),
        canLoad: [AuthGuard, ForceChangePasswordGuard],
        canActivate: [PrivilegeGuard],
        data: { privilege: Privileges.ShowsAdmin }
    },
    {
        path: 'admin/users',
        loadChildren: () => import('./admin/users/user-admin.module').then(m => m.UserAdminModule),
        canLoad: [AuthGuard, ForceChangePasswordGuard],
        canActivate: [PrivilegeGuard],
        data: { privilege: Privileges.UsersAdmin }
    },
    {
        path: 'admin/privileges',
        loadChildren: () => import('./admin/privileges/privilege-admin.module').then(m => m.PrivilegeAdminModule),
        canLoad: [AuthGuard, ForceChangePasswordGuard],
        canActivate: [PrivilegeGuard],
        data: { privilege: Privileges.PrivilegesAdmin }
    },
    {
        path: 'admin/templates',
        loadChildren: () => import('./admin/templates/templates-admin.module').then(m => m.TemplatesAdminModule),
        canLoad: [AuthGuard, ForceChangePasswordGuard, NoShowGuard],
        canActivate: [PrivilegeGuard, NoShowGuard],
        data: { privilege: Privileges.TemplateView }
    },
    {
        // Admin email tester: NO NoShowGuard — this tool can be used outside
        // the context of a specific show.
        path: 'admin/email-tester',
        loadComponent: () =>
            import('./admin/email-tester/email-tester.component').then(
                m => m.EmailTesterComponent
            ),
        canLoad: [AuthGuard, ForceChangePasswordGuard],
        canActivate: [PrivilegeGuard],
        data: { privilege: Privileges.EmailTestAdmin }
    },
    {
        // AuthGuard MUST be in canActivate (not canLoad): canLoad is ignored for
        // loadComponent routes, and PrivilegeGuard relies on AuthGuard having
        // awaited the auth state — otherwise a cold/direct navigation evaluates
        // the privilege before the user is loaded and bounces to /auth.
        path: 'admin/notification-tester',
        loadComponent: () =>
            import('./admin/notification-tester/notification-tester.component').then(
                m => m.NotificationTesterComponent
            ),
        canActivate: [AuthGuard, ForceChangePasswordGuard, PrivilegeGuard],
        data: { privilege: Privileges.EmailTestAdmin }
    }
];

@NgModule({
    imports: [
        RouterModule.forRoot(routes, {preloadingStrategy: PreloadAllModules})
    ],
    exports: [RouterModule]
})
export class AppRoutingModule {
    // Version info removed from console to prevent information disclosure (L-03)
    // Version is available via settings page for users who need it
}
